import { Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import { usePageVisibility } from "@/context/PageVisibilityContext";
import { Badge } from "@/components/ui/badge";
import { EyeOff } from "lucide-react";

/**
 * VisibilityGate — wraps a route. If the page is hidden and the current user
 * isn't an admin, redirect to home. Admins get a "Hidden from visitors" banner
 * so they can still test the page.
 */
const VisibilityGate = ({ pageKey, children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isVisible, loading: visLoading } = usePageVisibility();

  // Wait for BOTH auth AND visibility to load — render nothing to avoid a
  // premature redirect that would kick admins to home.
  if (authLoading || visLoading) return null;

  const visible = isVisible(pageKey);
  const isAdmin = user?.role === "admin";

  if (!visible && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {!visible && isAdmin && (
        <div
          className="bg-orange-500 text-white px-4 py-2 flex items-center gap-2 text-sm"
          data-testid="admin-hidden-banner"
        >
          <EyeOff className="w-4 h-4" />
          <span className="font-heading uppercase tracking-widest text-xs">
            Hidden from visitors
          </span>
          <Badge className="bg-white text-orange-500 hover:bg-white text-[10px]">
            Admin preview only
          </Badge>
          <span className="ml-auto text-xs">
            Toggle in <a href="/admin/pages" className="underline font-semibold">Admin → Pages</a>
          </span>
        </div>
      )}
      {children}
    </>
  );
};

export default VisibilityGate;
